import { memo, useState, useCallback, useRef } from 'react';
import { Handle, Position } from '@xyflow/react';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import type { IvrNodeType } from '@/api/ivrNodes';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { Plus, Edit2, Link2, Trash2, ChevronRight } from 'lucide-react';

// ── Border accent per node type ────────────────────────────────────────────────

function nodeAccentClass(type: IvrNodeType): string {
  if (type === 'playback') return 'border-l-blue-400';
  if (type === 'menu') return 'border-l-violet-400';
  if (type === 'transfer' || type === 'ring_group' || type === 'dial' || type === 'sip_connect')
    return 'border-l-orange-400';
  if (type === 'jump' || type === 'back' || type === 'repeat' || type === 'return_to_bookmark')
    return 'border-l-amber-400';
  if (type === 'conference' || type === 'voicemail' || type === 'listen_voicemail')
    return 'border-l-teal-400';
  if (type === 'survey' || type === 'quiz' || type === 'enrollment') return 'border-l-green-400';
  if (type === 'payment' || type === 'shopping') return 'border-l-emerald-400';
  return 'border-l-slate-300';
}

// ── Context menu ───────────────────────────────────────────────────────────────

interface ContextMenuProps {
  x: number;
  y: number;
  onEdit: () => void;
  onChangeParent: () => void;
  onAddChild: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function ContextMenu({ x, y, onEdit, onChangeParent, onAddChild, onDelete, onClose }: ContextMenuProps) {
  const items = [
    { icon: Edit2, label: 'Edit node', action: onEdit, className: '' },
    { icon: Link2, label: 'Change parent', action: onChangeParent, className: '' },
    { icon: Plus, label: 'Add child node', action: onAddChild, className: '' },
    { icon: Trash2, label: 'Delete node', action: onDelete, className: 'text-red-600 hover:bg-red-50' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      {/* Menu */}
      <div
        className="fixed z-50 min-w-[168px] rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-sm"
        style={{ left: x, top: y }}
      >
        {items.map(({ icon: Icon, label, action, className: cls }) => (
          <button
            key={label}
            type="button"
            className={cn(
              'flex w-full items-center gap-2.5 px-3 py-2 text-left text-slate-700 transition-colors hover:bg-slate-50',
              cls,
            )}
            onClick={() => { action(); onClose(); }}
          >
            <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            {label}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Node data shape ────────────────────────────────────────────────────────────

export interface IvrCanvasNodeData extends Record<string, unknown> {
  id: string;
  ref_number: string;
  name: string;
  node_type: IvrNodeType;
  active: boolean;
  isRoot: boolean;
  onEdit: (id: string) => void;
  onChangeParent: (id: string) => void;
  onAddChild: (id: string) => void;
  onDelete: (id: string) => void;
  onAddChildEdge: (fromId: string) => void;
}

// ── Main node component ────────────────────────────────────────────────────────

export const IvrCanvasNode = memo(function IvrCanvasNode({
  data,
  selected,
}: {
  data: IvrCanvasNodeData;
  selected?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handlePlusClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    data.onAddChildEdge(data.id);
  }, [data]);

  return (
    <>
      {/* Incoming handle — top center */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !border-2 !border-slate-300 !bg-white"
        isConnectable={false}
      />

      <div
        ref={nodeRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onContextMenu={handleContextMenu}
        onClick={() => data.onEdit(data.id)}
        className={cn(
          // base layout
          'relative w-[220px] rounded-lg border bg-white shadow-sm',
          // left accent stripe
          'border-l-4',
          nodeAccentClass(data.node_type),
          // selection / hover ring
          selected
            ? 'border-t-indigo-400 border-r-indigo-400 border-b-indigo-400 shadow-md ring-2 ring-indigo-200'
            : 'border-t-slate-200 border-r-slate-200 border-b-slate-200 hover:border-t-slate-300 hover:shadow-md',
          // inactive dimming
          !data.active && 'opacity-60',
          'cursor-pointer transition-all duration-100 select-none',
        )}
      >
        {/* Root badge */}
        {data.isRoot && (
          <div className="absolute -top-2.5 left-3">
            <span className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 shadow-sm">
              Root
            </span>
          </div>
        )}

        <div className="px-3 py-2.5 space-y-1.5">
          {/* Header row: ref_number + active dot */}
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] font-semibold text-slate-400 tabular-nums">
              {formatRef(data.ref_number)}
            </span>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  data.active ? 'bg-emerald-400' : 'bg-slate-300',
                )}
                title={data.active ? 'Active' : 'Inactive'}
              />
            </div>
          </div>

          {/* Node name */}
          <p className="text-[13px] font-semibold text-slate-800 leading-snug line-clamp-2">
            {data.name}
          </p>

          {/* Node type badge */}
          <div className="flex items-center gap-1.5">
            <NodeTypeBadge type={data.node_type} />
          </div>
        </div>

        {/* Hover: quick action hint */}
        {hovered && !selected && (
          <div className="absolute inset-x-0 bottom-0 flex justify-end px-2 pb-1.5">
            <span className="flex items-center gap-1 text-[10px] text-slate-300">
              <Edit2 className="h-2.5 w-2.5" /> click to edit
            </span>
          </div>
        )}
      </div>

      {/* Outgoing handle — bottom center */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !border-2 !border-slate-300 !bg-white"
        isConnectable={false}
      />

      {/* + button at bottom — appears on hover */}
      {hovered && (
        <button
          type="button"
          onClick={handlePlusClick}
          className={cn(
            'absolute -bottom-7 left-1/2 -translate-x-1/2',
            'flex h-6 w-6 items-center justify-center rounded-full',
            'border-2 border-indigo-300 bg-white text-indigo-500 shadow-sm',
            'hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600',
            'transition-all duration-100 z-10',
          )}
          title="Add child node"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={() => data.onEdit(data.id)}
          onChangeParent={() => data.onChangeParent(data.id)}
          onAddChild={() => data.onAddChildEdge(data.id)}
          onDelete={() => data.onDelete(data.id)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Right-click hint (tiny, only when hovered and no menu open) */}
      {hovered && !contextMenu && (
        <div className="absolute -right-1 top-1/2 -translate-y-1/2 translate-x-full pl-2 pointer-events-none">
          <div className="flex items-center gap-0.5 text-[9px] text-slate-300 whitespace-nowrap">
            <ChevronRight className="h-2.5 w-2.5" />
            right-click
          </div>
        </div>
      )}
    </>
  );
});
